import { useEffect, useRef, useState } from 'react'
import useIsMobile from '../../../../utils/detectDevice'
import {
  useGetNotifications,
  useRemoveNotification,
  useUpdateNotificationRead,
} from '../../../../queries/notificationApi'
import { useNavigate } from 'react-router-dom'
import { Bell, Download, Calendar, X, Loader2, MessageSquareMore, FolderDown } from 'lucide-react'
import { queryClient } from '../../../../queries/client'
import { clearAccountScopedSession, getActiveAccount } from '../../../../utils/SessionStorageUtils'

interface NotificationEventData {
  fileUrl: string
  fileName: string
  fileSize: number
}

interface Notification {
  id: string
  eventData: any
  userId: string
  eventType: string
  eventLevel: string
  timestamp: string
  message: string
  expiry: string
  isRead: boolean
}

type NotificationObject = {
  id: string
  data: Notification
  label: string
  secondaryLabel?: string
  isDownloadable: boolean
  expiry: string
  timestamp: string
  eventType: string
  eventLevel: string
  showExpiry?: boolean
}

export const NotificationPopup: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [currentNotifications, setCurrentNotifications] = useState<NotificationObject[]>([])
  const [currentCommentRef, setCurrentCommentRef] = useState<string | undefined>(undefined)
  const [newNotifications, setNewNotifications] = useState(0)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [downloadingIds, setDownloadingIds] = useState<string[]>([])
  const popoverRef = useRef<HTMLDivElement>(null)
  const [notificationTaskId, setNotificationTaskId] = useState<string | undefined>(undefined)
  const [routeTask, setRouteTask] = useState(false)
  const { isMobile } = useIsMobile(1024)
  const { mutate: removeNotificationMutation } = useRemoveNotification()
  const { mutate: updateNotificationReadMutation } = useUpdateNotificationRead()

  // Add CSS animations
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      .notification-item:hover .remove-btn-desktop {
        opacity: 1 !important;
      }
      .notification-badge {
        top: -4px;
        right: -4px;
        min-width: 20px;
        height: 20px;
        animation: pulse 2s infinite;
      }
      .notification-dropdown {
        width: 240px;
        right: -80px;
      }
      @media (min-width: 576px) {
        .notification-dropdown {
          width: 384px;
          right: 0;
        }
      }
      .notification-card-body {
        max-height: 384px;
      }
      .notification-list-item {
        margin: 8px 0;
        padding: 16px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      .notification-icon {
        height: 32px;
        width: 32px;
        padding: 6px;
      }
      .notification-time-icon {
        height: 12px;
        width: 12px;
        margin-right: 4px;
      }
      .bell-icon, .loader-icon {
        height: 20px;
        width: 20px;
      }
      .large-bell-icon {
        height: 48px;
        width: 48px;
      }
      .large-loader-icon {
        height: 32px;
        width: 32px;
      }
      .download-icon, .close-icon {
        height: 16px;
        width: 16px;
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  const navigate = useNavigate()

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (seconds < 60) return `${seconds} seconds ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`
    const days = Math.floor(hours / 24)
    return `${days} day${days !== 1 ? 's' : ''} ago`
  }

  const handleDownload = async (notification: Notification) => {
    try {
      setDownloadingIds((prev) => [...prev, notification.id])
      const { fileUrl, fileName } = notification.eventData

      await new Promise((resolve) => setTimeout(resolve, 1000))

      const link = document.createElement('a')
      link.href = fileUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
    } finally {
      setDownloadingIds((prev) => prev.filter((id) => id !== notification.id))
    }
  }

  const removeNotification = (id: string, projectId: string) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id))
    setCurrentNotifications((prev) => prev.filter((notification) => notification.id !== id))
    if (currentNotifications.length === 1) {
      setNewNotifications(0)
    }
    removeNotificationMutation({ notificationId: id })
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsPopoverOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const { data, refetch } = useGetNotifications()

  useEffect(() => {
    if (data) {
      setNotifications(data.data)
      setCurrentNotifications(
        data.data.map((notification: Notification) => ({
          id: notification.id,
          data: notification,
          label:
            notification.eventType === 'DOWNLOAD'
              ? notification.eventData.fileName
              : notification.message,
          secondaryLabel:
            notification.eventType === 'DOWNLOAD'
              ? formatFileSize(notification.eventData.fileSize)
              : '',
          isDownloadable: notification.eventType === 'DOWNLOAD',
          expiry: notification.expiry,
          timestamp: notification.timestamp,
          eventType: notification.eventType,
          eventLevel: notification.eventLevel,
          showExpiry: notification.eventType === 'DOWNLOAD' ? true : false,
        }))
      )
    }
  }, [data])

  const accessToken = sessionStorage.getItem('accessToken') || ''
  useEffect(() => {
    const eventSource = new EventSource(
      `${
        process.env.REACT_APP_API_URL || 'http://localhost:5002/v1'
      }/events?AccessToken=${accessToken}`
    )

    eventSource.addEventListener('DOWNLOAD', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data)
        handleNotification(data)
      } catch (e) {}
    })

    // A different session (another tab/browser/device) switched the active
    // account. Not a notification-bell entry — just re-sync this session so
    // it doesn't keep operating against the account it thinks is still active.
    eventSource.addEventListener('ACCOUNT_SWITCHED', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data)
        const activeAccount = getActiveAccount()
        if (data?.accountId && String(data.accountId) !== String(activeAccount?.id)) {
          clearAccountScopedSession({ id: String(data.accountId), name: data.accountName })
          queryClient.clear()
          window.location.reload()
        }
      } catch (e) {}
    })

    const handleNotification = (data: any) => {
      if (data) {
        setNewNotifications((prev) => prev + 1)
        queryClient.invalidateQueries('getNotifications')
      }
    }

    eventSource.onerror = (error) => {
      eventSource.close()
    }

    return () => {
      eventSource.close()
    }
  }, [])

  useEffect(() => {
    if (currentNotifications && currentNotifications.length > 0) {
      const unreadNotification = currentNotifications.filter(
        (notification) => !notification.data.isRead
      )
      if (unreadNotification.length > 0) setNewNotifications(unreadNotification.length)
      else setNewNotifications(0)
    }
  }, [currentNotifications])

  const handlePopoverToggle = () => {
    setIsPopoverOpen(!isPopoverOpen)
    if (!isPopoverOpen) {
      updateNotificationReadMutation()
      refetch()
    }
  }

  return (
    <div className='position-relative'>
      <div ref={popoverRef}>
        {/* <button type='button' className='border-0 bg-transparent ' onClick={handlePopoverToggle}>
          <Bell className='text-gray-700 text-hover-primary bell-icon d-flex mt-lg-6 align-items-center justify-content-center' />
          {newNotifications > 0 && (
            <span className='badge bg-primary rounded-pill position-absolute top-10 fs-8 d-flex align-items-center justify-content-center notification-badge'>
              {newNotifications}
            </span>
          )}
        </button> */}

        <button
          type='button'
          className='border-0 bg-transparent d-flex align-items-center justify-content-center position-relative p-0 mt-lg-2'
          onClick={handlePopoverToggle}
          style={{ width: '44px', height: '44px' }}
        >
          <span className='position-relative d-inline-flex align-items-center justify-content-center'>
            <Bell className='text-gray-700 fs-2' />
            {newNotifications > 0 && (
              <span
                className='badge bg-primary rounded-circle position-absolute top-0 start-100 translate-middle'
                style={{ fontSize: '0.85rem', minWidth: '20px' }}
              >
                {newNotifications}
              </span>
            )}
          </span>
        </button>

        {isPopoverOpen && (
          <div className='card shadow-lg border position-absolute notification-dropdown rounded-3 mt-3'>
            <div className='bg-white d-flex justify-content-between align-items-center border-bottom p-4 rounded-top'>
              <p className='fs-3 mb-0 text-gray-700 fw-bold'>Notifications</p>
              <button
                onClick={() => setIsPopoverOpen(false)}
                className='btn btn-sm btn-light rounded-pill p-1 border-0'
                aria-label='Close notifications'
              >
                <X className='close-icon' />
              </button>
            </div>

            <div className='card-body p-0 overflow-auto notification-card-body'>
              {isLoading ? (
                <div className='text-center p-5'>
                  <Loader2 className='text-primary mx-auto large-loader-icon' />
                  <p className='text-muted mt-2 mb-0'>Loading notifications...</p>
                </div>
              ) : currentNotifications.length === 0 ? (
                <div className='text-center p-5'>
                  <Bell className='text-muted mb-3 mx-auto d-block large-bell-icon' />
                  <p className='text-muted mb-0'>No notifications available</p>
                </div>
              ) : (
                <ul className='list-unstyled mb-0 px-4'>
                  {currentNotifications
                    .sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp))
                    .map((notification) => (
                      <li
                        key={notification.id}
                        className={`d-flex w-100 position-relative rounded notification-item notification-list-item bg-light ${
                          notification.eventType !== 'DOWNLOAD' ? 'cursor-pointer' : ''
                        }`}
                      >
                        <div className='d-flex w-100'>
                          <div className='flex-shrink-0 d-flex align-items-center'>
                            {notification.eventType === 'DOWNLOAD' ? (
                              <FolderDown className='text-primary bg-white rounded shadow-sm notification-icon' />
                            ) : (
                              <MessageSquareMore className='text-primary bg-white rounded shadow-sm notification-icon' />
                            )}
                          </div>

                          <div className='flex-grow-1 min-w-0 ps-3 overflow-hidden'>
                            <p className='mb-1 fw-medium text-dark fs-7 lh-sm overflow-hidden'>
                              <span className='d-block text-truncate w-100'>
                                {notification.label.split('/').pop()}
                              </span>
                            </p>

                            <p className='text-muted fs-8 mb-1'>{notification?.secondaryLabel}</p>

                            <div className='d-flex align-items-center text-muted fs-8 gap-3 mt-2'>
                              <span className='d-flex align-items-center'>
                                <Calendar className='notification-time-icon' />
                                {formatTimeAgo(notification.timestamp)}
                              </span>
                            </div>

                            {notification.showExpiry && (
                              <p className='text-danger mb-0 fs-8 mt-1'>
                                Expires: {new Date(notification.expiry).toLocaleString()}
                              </p>
                            )}
                          </div>

                          <div className='d-flex align-items-start gap-1 ms-2'>
                            {notification.isDownloadable && (
                              <button
                                onClick={() => handleDownload(notification.data)}
                                disabled={downloadingIds.includes(notification.id)}
                                className={`btn btn-sm rounded-pill p-1 border-0 ${
                                  downloadingIds.includes(notification.id)
                                    ? 'text-muted'
                                    : 'text-primary'
                                }`}
                                title='Download file'
                              >
                                {downloadingIds.includes(notification.id) ? (
                                  <Loader2 className='download-icon' />
                                ) : (
                                  <Download className='download-icon' />
                                )}
                              </button>
                            )}

                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                removeNotification(
                                  notification.id,
                                  notification?.data?.eventData?.projectId
                                )
                              }}
                              className='btn btn-sm text-muted rounded-pill p-1 border-0 d-block d-sm-none'
                              title='Remove notification'
                            >
                              <X className='close-icon' />
                            </button>

                            {/* Desktop version with hover effect */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                removeNotification(
                                  notification.id,
                                  notification?.data?.eventData?.projectId
                                )
                              }}
                              className='btn btn-sm text-muted rounded-pill p-1 border-0 d-none d-sm-block remove-btn-desktop opacity-0'
                              title='Remove notification'
                            >
                              <X className='close-icon' />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
